import React, { useState, useEffect } from 'react'
import { Loader2, Mail, ArrowLeft, AlertCircle, X, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { authAPI } from '../../services/authAPI'

// CheckEmailModal Component - Updated with better messaging
export const CheckEmailModal = ({
  isOpen,
  onClose,
  onResend,
  identifier,
  identifierType = 'email', // 'email' or 'phone'
  isResending = false
}) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir && i18n.dir() === 'rtl'
  const [cooldown, setCooldown] = useState(0)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState('')
  const [localResending, setLocalResending] = useState(false)

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setCooldown(0)
      setResendSuccess(false)
      setResendError('')
    }
  }, [isOpen])

  // Handle cooldown timer
  useEffect(() => {
    let timer
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [cooldown])

  // Enhanced resend handler with cooldown and feedback
  const handleResend = async () => {

    if (cooldown > 0 || isResending || localResending) {
      return
    }

    // Set loading state immediately
    setLocalResending(true)
    setResendError('')
    setResendSuccess(false)

    try {
      const baseURL = (import.meta.env.VITE_API_URL)
      const endpoint = `${baseURL}/api/v1/auth/password/send-reset-link`

      // Determine if identifier is email or phone
      const isEmail = identifier.includes('@')
      const requestBody = isEmail 
        ? { email: identifier }
        : { identifier: identifier } // For phone, try identifier (backend needs to support this)

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      })

      // Check if response is not ok
      if (!response.ok) {
        const errorData = await response.json()
        console.error('Resend API error:', errorData)
        
        // Show the actual error message from the response
        if (errorData.errors?.message) {
          setResendError(errorData.errors.message)
        } else if (errorData.message) {
          setResendError(errorData.message)
        } else {
          setResendError(t('forgotPassword.checkEmail.errors.resendFailed'))
        }
        
        // Set cooldown if it's a throttled error
        if (errorData.errors?.error_type === 'throttled') {
          setCooldown(60)
        }
        return
      }

      // Success case
      setResendSuccess(true)
      setCooldown(60) // 60 second cooldown
      // Clear success message after 3 seconds
      setTimeout(() => setResendSuccess(false), 3000)
    } catch (error) {
      console.error('onResend failed:', error)
      setResendError(error.message || t('forgotPassword.checkEmail.errors.resendFailed'))
    } finally {
      setLocalResending(false)
    }
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      <div style={{
        width: '90%',
        maxWidth: '500px',
        background: '#FFFFFF',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        textAlign: 'center',
        position: 'relative',
        animation: 'modalSlideIn 0.3s ease-out'
      }}>
        {/* Animated Email Icon */}
        <div style={{
          width: '100px',
          height: '100px',
          backgroundColor: '#dbeafe',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 32px',
          animation: 'bounce 2s infinite'
        }}>
          <Mail size={50} color="#3b82f6" />
        </div>

        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '16px'
        }}>
          {t('forgotPassword.checkEmail.title')}
        </h1>

        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          marginBottom: '24px',
          lineHeight: '1.6'
        }}>
          {identifierType === 'email' 
            ? t('forgotPassword.checkEmail.message', { defaultValue: 'We\'ve sent a password reset link to' })
            : t('forgotPassword.checkEmail.messagePhone', { defaultValue: 'We\'ve sent a password reset code to' })}{' '}
          <strong>{identifier}</strong>
        </p>

        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px',
          textAlign: 'left'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#0369a1',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Mail
              size={18}
              style={isRTL ? { marginLeft: '8px' } : { marginRight: '8px' }}
            />
            {t('forgotPassword.checkEmail.whatsInside.title')}
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            fontSize: '14px',
            color: '#0369a1',
            lineHeight: '1.5'
          }}>
            <li>{t('forgotPassword.checkEmail.whatsInside.items.link')}</li>
            <li>{t('forgotPassword.checkEmail.whatsInside.items.validity')}</li>
          </ul>
        </div>

        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '32px',
          fontSize: '14px',
          color: '#92400e',
          textAlign: 'left'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <AlertCircle
              size={16}
              style={isRTL ? { marginLeft: '8px', marginTop: '2px', flexShrink: 0 } : { marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
            />
            <div>
              <strong>{t('forgotPassword.checkEmail.notFound.title')}</strong>{' '}
              {t('forgotPassword.checkEmail.notFound.body', {
                email: import.meta.env.VITE_FROM_EMAIL || 'noreply@sulhafa.com'
              })}
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {resendSuccess && (
          <div style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #a7f3d0',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#065f46',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '8px',
                flexShrink: 0
              }}>
                <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
              </div>
              <div>
                <strong>{t('forgotPassword.checkEmail.success.title')}</strong>{' '}
                {t('forgotPassword.checkEmail.success.body')}
              </div>
            </div>
          </div>
        )}

        {resendError && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#991b1b',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <AlertCircle size={16} style={{ marginRight: '8px', flexShrink: 0 }} />
              <div>{resendError}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => {
              handleResend()
            }}
            disabled={isResending || localResending || cooldown > 0}
                          style={{
                height: '48px',
                padding: '0 24px',
                backgroundColor: (isResending || localResending || cooldown > 0) ? '#9ca3af' : '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                cursor: (isResending || localResending || cooldown > 0) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '140px'
              }}
                          onMouseEnter={(e) => {
                if (!isResending && !localResending && cooldown === 0) {
                  e.target.style.backgroundColor = '#2563eb'
                }
              }}
              onMouseLeave={(e) => {
                if (!isResending && !localResending && cooldown === 0) {
                  e.target.style.backgroundColor = '#3b82f6'
                }
              }}
                      >
              {(isResending || localResending) ? (
                <>
                  <Loader2
                    size={18}
                    style={{
                      ...(isRTL ? { marginLeft: '8px' } : { marginRight: '8px' }),
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  {t('forgotPassword.checkEmail.buttons.sending')}
                </>
              ) : cooldown > 0 ? (
                <>
                  <Loader2
                    size={18}
                    style={isRTL ? { marginLeft: '8px' } : { marginRight: '8px' }}
                  />
                  {cooldown}s
                </>
              ) : (
                t('forgotPassword.checkEmail.buttons.resend')
              )}
            </button>

          <button
            onClick={onClose}
            style={{
              height: '48px',
              padding: '0 24px',
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#d1d5db'
              e.target.style.backgroundColor = '#f9fafb'
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e5e7eb'
              e.target.style.backgroundColor = 'transparent'
            }}
          >
            <ArrowLeft size={18} style={isRTL ? { marginLeft: '8px' } : { marginRight: '8px' }} />
            {t('forgotPassword.checkEmail.buttons.back')}
          </button>
        </div>

        {/* Cooldown Info */}
        {cooldown > 0 && (
          <p style={{
            fontSize: '12px',
            color: '#9ca3af',
            marginTop: '12px',
            marginBottom: 0
          }}>
            {t('forgotPassword.checkEmail.cooldown', { seconds: cooldown })}
          </p>
        )}

        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '-10px',
          left: '-10px',
          width: '20px',
          height: '20px',
          backgroundColor: '#fbbf24',
          borderRadius: '50%',
          animation: 'float1 4s ease-in-out infinite'
        }} />

        <div style={{
          position: 'absolute',
          top: '20px',
          right: '-5px',
          width: '15px',
          height: '15px',
          backgroundColor: '#8b5cf6',
          borderRadius: '50%',
          animation: 'float2 3s ease-in-out infinite'
        }} />

        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '20px',
          width: '12px',
          height: '12px',
          backgroundColor: '#f97316',
          borderRadius: '50%',
          animation: 'float3 5s ease-in-out infinite'
        }} />

        {/* Animation styles */}
        <style>
          {`
            @keyframes modalSlideIn {
              from {
                opacity: 0;
                transform: scale(0.9) translateY(-20px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
            
            @keyframes bounce {
              0%, 20%, 50%, 80%, 100% {
                transform: translateY(0);
              }
              40% {
                transform: translateY(-10px);
              }
              60% {
                transform: translateY(-5px);
              }
            }
            
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
            
            @keyframes float1 {
              0%, 100% {
                transform: translateY(0px) rotate(0deg);
              }
              50% {
                transform: translateY(-20px) rotate(180deg);
              }
            }
            
            @keyframes float2 {
              0%, 100% {
                transform: translateY(0px) rotate(0deg);
              }
              50% {
                transform: translateY(-15px) rotate(-180deg);
              }
            }
            
            @keyframes float3 {
              0%, 100% {
                transform: translateY(0px) rotate(0deg);
              }
              50% {
                transform: translateY(-25px) rotate(180deg);
              }
            }
          `}
        </style>
      </div>
    </div>
  )
}

// ForgotPasswordModal Component
export const ForgotPasswordModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  error = '',
  onSuccess
}) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir && i18n.dir() === 'rtl'
  const [activeTab, setActiveTab] = useState('email') // 'email' or 'phone'
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [selectedCountryCode, setSelectedCountryCode] = useState('+966')
  const [isCountryCodeOpen, setIsCountryCodeOpen] = useState(false)
  const [countrySearchTerm, setCountrySearchTerm] = useState('')

  // Country codes with flags (same as EditProfileModal)
  const countryCodes = [
    { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
    { code: '+971', flag: '🇦🇪', name: 'UAE' },
    { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
    { code: '+974', flag: '🇶🇦', name: 'Qatar' },
    { code: '+965', flag: '🇰🇼', name: 'Kuwait' },
    { code: '+968', flag: '🇴🇲', name: 'Oman' },
    { code: '+962', flag: '🇯🇴', name: 'Jordan' },
    { code: '+961', flag: '🇱🇧', name: 'Lebanon' },
    { code: '+20', flag: '🇪🇬', name: 'Egypt' },
    { code: '+212', flag: '🇲🇦', name: 'Morocco' },
    { code: '+216', flag: '🇹🇳', name: 'Tunisia' },
    { code: '+213', flag: '🇩🇿', name: 'Algeria' },
    { code: '+1', flag: '🇺🇸', name: 'USA' },
    { code: '+44', flag: '🇬🇧', name: 'UK' },
    { code: '+33', flag: '🇫🇷', name: 'France' },
    { code: '+49', flag: '🇩🇪', name: 'Germany' },
    { code: '+91', flag: '🇮🇳', name: 'India' },
    { code: '+86', flag: '🇨🇳', name: 'China' },
    { code: '+81', flag: '🇯🇵', name: 'Japan' },
    { code: '+82', flag: '🇰🇷', name: 'South Korea' },
  ]

  // Handle click outside to close country code dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCountryCodeOpen && !event.target.closest('.country-code-container')) {
        setIsCountryCodeOpen(false)
        setCountrySearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCountryCodeOpen])

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setPhone('')
      setEmailError('')
      setPhoneError('')
      setActiveTab('email')
      setSelectedCountryCode('+966')
      setIsCountryCodeOpen(false)
      setCountrySearchTerm('')
    }
  }, [isOpen])

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Phone validation (validates only the phone number part, not including country code)
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '')
    // At least 7 digits and at most 15 digits for the phone number itself
    // (Country code is separate, so we don't count it here)
    return digitsOnly.length >= 7 && digitsOnly.length <= 15
  }

  const handleEmailChange = (e) => {
    const value = e.target.value
    setEmail(value)
    if (emailError) {
      setEmailError('')
    }
  }

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9\+\-\s]/g, '')
    setPhone(value)
    if (phoneError) {
      setPhoneError('')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (activeTab === 'email') {
      // Validate email
      if (!email.trim()) {
        setEmailError(t('forgotPassword.form.errors.required'))
        return
      }

      if (!validateEmail(email.trim())) {
        setEmailError(t('forgotPassword.form.errors.invalidEmail'))
        return
      }

      // Call the submit handler with email
      const result = await onSubmit(email.trim(), 'email')

      // If successful, show success modal
      if (result?.success) {
        onSuccess(email.trim(), 'email')
      }
    } else {
      // Validate phone
      if (!phone.trim()) {
        setPhoneError(t('forgotPassword.form.errors.required'))
        return
      }

      // Validate phone number (without country code)
      if (!validatePhone(phone.trim())) {
        setPhoneError(t('forgotPassword.form.errors.invalidPhone'))
        return
      }

      // Combine country code with phone number
      const fullPhone = `${selectedCountryCode} ${phone.trim()}`.trim()

      // Call the submit handler with phone
      const result = await onSubmit(fullPhone, 'phone')

      // If successful, show success modal
      if (result?.success) {
        onSuccess(fullPhone, 'phone')
      }
    }
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      <div style={{
        width: '90%',
        maxWidth: '500px',
        background: '#FFFFFF',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        animation: 'modalSlideIn 0.3s ease-out'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            width: '32px',
            height: '32px',
            border: 'none',
            background: '#f3f4f6',
            borderRadius: '50%',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            opacity: isLoading ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.background = '#e5e7eb'
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.background = '#f3f4f6'
            }
          }}
        >
          <X size={18} color="#6b7280" />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#dbeafe',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            {activeTab === 'email' ? (
              <Mail size={40} color="#3b82f6" />
            ) : (
              <Phone size={40} color="#3b82f6" />
            )}
          </div>

          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            {t('forgotPassword.form.title')}
          </h1>

          <p style={{
            fontSize: '16px',
            color: '#6b7280'
          }}>
            {t('forgotPassword.form.subtitle')}
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          marginBottom: '24px',
          borderBottom: '2px solid #F3F4F6'
        }}>
          <button
            type="button"
            onClick={() => {
              setActiveTab('email')
              setEmailError('')
            }}
            style={{
              flex: 1,
              padding: '10px',
              background: 'transparent',
              border: 'none',
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              color: activeTab === 'email' ? '#3b82f6' : '#9CA3AF',
              borderBottom: activeTab === 'email' ? '3px solid #3b82f6' : '3px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {t('login.tabs.email')}
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('phone')
              setPhoneError('')
            }}
            style={{
              flex: 1,
              padding: '10px',
              background: 'transparent',
              border: 'none',
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              color: activeTab === 'phone' ? '#3b82f6' : '#9CA3AF',
              borderBottom: activeTab === 'phone' ? '3px solid #3b82f6' : '3px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {t('login.tabs.mobile')}
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {activeTab === 'email' ? (
            /* Email Input */
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t('forgotPassword.form.emailLabel')}
              </label>
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder={t('forgotPassword.form.emailPlaceholder')}
                disabled={isLoading}
                style={{
                  width: '100%',
                  height: '52px',
                  padding: '0 16px',
                  border: (error || emailError) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!error && !emailError) {
                    e.target.style.borderColor = '#3b82f6'
                  }
                }}
                onBlur={(e) => {
                  if (!error && !emailError) {
                    e.target.style.borderColor = '#e5e7eb'
                  }
                }}
              />
              {(emailError || error) && (
                <p style={{
                  fontSize: '14px',
                  color: '#ef4444',
                  marginTop: '4px'
                }}>
                  {emailError || error}
                </p>
              )}
            </div>
          ) : (
            /* Phone Input with Country Code */
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t('forgotPassword.form.phoneLabel', { defaultValue: 'Phone Number' })}
              </label>
              <div style={{ position: 'relative', display: 'flex' }}>
                <div
                  className="country-code-container"
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '0 16px',
                    borderTop: (error || phoneError) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    borderBottom: (error || phoneError) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    borderLeft: (error || phoneError) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    borderRight: 'none',
                    borderRadius: '12px 0 0 12px',
                    background: '#F9FAFB',
                    minWidth: '120px',
                    height: '52px',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease',
                  }}
                  onClick={() => {
                    setIsCountryCodeOpen(!isCountryCodeOpen)
                    if (!isCountryCodeOpen) {
                      setCountrySearchTerm('')
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isCountryCodeOpen && !error && !phoneError) {
                      e.currentTarget.style.borderTopColor = '#3b82f6'
                      e.currentTarget.style.borderBottomColor = '#3b82f6'
                      e.currentTarget.style.borderLeftColor = '#3b82f6'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCountryCodeOpen && !error && !phoneError) {
                      e.currentTarget.style.borderTopColor = '#e5e7eb'
                      e.currentTarget.style.borderBottomColor = '#e5e7eb'
                      e.currentTarget.style.borderLeftColor = '#e5e7eb'
                    }
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {countryCodes.find(c => c.code === selectedCountryCode)?.flag || '🇸🇦'}
                  </span>
                  <span style={{ fontSize: '16px', color: '#374151', fontWeight: 500 }}>
                    {selectedCountryCode}
                  </span>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    style={{
                      color: '#9CA3AF',
                      transform: isCountryCodeOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}
                  >
                    <path d="m7 10 5 5 5-5z" />
                  </svg>
                  
                  {/* Country Code Dropdown */}
                  {isCountryCodeOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '2px solid #E5E7EB',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        marginTop: '4px',
                      }}
                    >
                      {/* Search Input */}
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                        <input
                          type="text"
                          placeholder={t('forgotPassword.form.searchCountry', { defaultValue: 'Search country...' })}
                          value={countrySearchTerm}
                          onChange={(e) => setCountrySearchTerm(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'border-color 0.2s ease',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#3b82f6'
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#E5E7EB'
                          }}
                        />
                      </div>
                      
                      {countryCodes
                        .filter(country => 
                          country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
                          country.code.includes(countrySearchTerm)
                        )
                        .map((country) => (
                        <div
                          key={country.code}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            borderBottom: '1px solid #F3F4F6',
                          }}
                          onClick={() => {
                            setSelectedCountryCode(country.code)
                            setIsCountryCodeOpen(false)
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#F9FAFB'
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'white'
                          }}
                        >
                          <span style={{ fontSize: '20px' }}>{country.flag}</span>
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                            {country.code}
                          </span>
                          <span style={{ fontSize: '12px', color: '#6B7280', marginLeft: 'auto' }}>
                            {country.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder={t('forgotPassword.form.phonePlaceholder', { defaultValue: 'Enter your phone number' })}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    height: '52px',
                    padding: '0 16px',
                    borderTop: (error || phoneError) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    borderBottom: (error || phoneError) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    borderRight: (error || phoneError) ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    borderLeft: 'none',
                    borderRadius: '0 12px 12px 0',
                    fontSize: '16px',
                    color: '#333333',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}
                  onFocus={(e) => {
                    if (!error && !phoneError) {
                      e.target.style.borderTopColor = '#3b82f6'
                      e.target.style.borderBottomColor = '#3b82f6'
                      e.target.style.borderRightColor = '#3b82f6'
                      const countryCodeDiv = e.target.previousElementSibling
                      if (countryCodeDiv) {
                        countryCodeDiv.style.borderTopColor = '#3b82f6'
                        countryCodeDiv.style.borderBottomColor = '#3b82f6'
                        countryCodeDiv.style.borderLeftColor = '#3b82f6'
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (!error && !phoneError) {
                      e.target.style.borderTopColor = '#e5e7eb'
                      e.target.style.borderBottomColor = '#e5e7eb'
                      e.target.style.borderRightColor = '#e5e7eb'
                      const countryCodeDiv = e.target.previousElementSibling
                      if (countryCodeDiv) {
                        countryCodeDiv.style.borderTopColor = '#e5e7eb'
                        countryCodeDiv.style.borderBottomColor = '#e5e7eb'
                        countryCodeDiv.style.borderLeftColor = '#e5e7eb'
                      }
                    }
                  }}
                />
              </div>
              {(phoneError || error) && (
                <p style={{
                  fontSize: '14px',
                  color: '#ef4444',
                  marginTop: '4px'
                }}>
                  {phoneError || error}
                </p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              height: '56px',
              backgroundColor: isLoading ? '#9ca3af' : '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#2563eb'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#3b82f6'
              }
            }}
          >
            {isLoading ? (
              <>
                <Loader2
                  size={20}
                  style={{
                    ...(isRTL ? { marginLeft: '8px' } : { marginRight: '8px' }),
                    animation: 'spin 1s linear infinite'
                  }}
                />
                {t('forgotPassword.form.buttons.sending')}
              </>
            ) : (
              t('forgotPassword.form.buttons.submit')
            )}
          </button>
        </form>

        {/* Animation styles */}
        <style>
          {`
            @keyframes modalSlideIn {
              from {
                opacity: 0;
                transform: scale(0.9) translateY(-20px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
            
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </div>
    </div>
  )
}

/**
 * Demo Component for testing the ForgotPasswordModal
 * 
 * This component demonstrates the complete forgot password flow:
 * 1. User enters email in ForgotPasswordModal
 * 2. API call to request password reset (with fallback to mock)
 * 3. Shows CheckEmailModal with resend functionality
 * 4. Smart API integration with automatic fallback
 * 
 * Features:
 * - Real API calls when backend is available
 * - Automatic fallback to mock mode when API is unavailable
 * - Comprehensive error handling (404, 429, 422, network errors)
 * - 60-second cooldown timer for resend to prevent spam
 * - Success/error feedback messages
 * - Loading states and proper UX
 * 
 * Usage:
 * <ForgotPasswordDemo />
 */
export const ForgotPasswordDemo = () => {

  const [showForgotModal, setShowForgotModal] = useState(false)
  const [showCheckEmailModal, setShowCheckEmailModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userIdentifierType, setUserIdentifierType] = useState('email')

  // Test useEffect to verify component is working
  useEffect(() => {
  }, [])

  // Monitor userEmail changes
  useEffect(() => {
  }, [userEmail])

  // Real forgot password API call with fallback to mock
  const handleForgotPasswordSubmit = async (identifier, identifierType) => {
    setIsLoading(true)
    setError('')

    try {
      // Try to call the real API first - use requestPasswordReset which accepts identifier
      await authAPI.requestPasswordReset(identifier.trim())

      // Success - store identifier and type, then show success modal
      setUserEmail(identifier)
      setUserIdentifierType(identifierType || 'email')
      setShowForgotModal(false)
      setShowCheckEmailModal(true)

      return { success: true }

    } catch (error) {
      console.error('Password reset request failed:', error)

      // If it's a network error or API not available, fall back to mock
      if (error.code === 'NETWORK_ERROR' || !error.response || error.response?.status === 0) {

        // Mock validation for demo purposes
        const validEmails = ['test@example.com', 'demo@example.com', 'user@example.com']
        const validPhones = ['+966501234567', '+1234567890', '+971501234567']
        
        if (identifierType === 'email' && !validEmails.includes(identifier.toLowerCase())) {
          setError('We couldn\'t find an account with that email. Please check your email address and try again.')
          return { success: false }
        }
        
        if (identifierType === 'phone' && !validPhones.some(phone => identifier.replace(/\D/g, '').includes(phone.replace(/\D/g, '')))) {
          setError('We couldn\'t find an account with that phone number. Please check your phone number and try again.')
          return { success: false }
        }

        // Mock success
        setUserEmail(identifier)
        setUserIdentifierType(identifierType || 'email')
        setShowForgotModal(false)
        setShowCheckEmailModal(true)

        return { success: true }
      }

      // Handle specific error response structure
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Show the actual error message from the response
        if (errorData.errors?.message) {
          setError(errorData.errors.message)
        } else if (errorData.message) {
          setError(errorData.message)
        } else {
          setError('Something went wrong. Please try again later.')
        }
      } else {
        // Handle other error cases
        if (error.response?.status === 404) {
          setError(identifierType === 'email' 
            ? 'We couldn\'t find an account with that email. Please check your email address and try again.'
            : 'We couldn\'t find an account with that phone number. Please check your phone number and try again.')
        } else if (error.response?.status === 429) {
          setError('Too many requests. Please wait a moment before trying again.')
        } else if (error.response?.status === 422) {
          setError(identifierType === 'email'
            ? 'Please enter a valid email address.'
            : 'Please enter a valid phone number.')
        } else {
          setError('Something went wrong. Please try again later.')
        }
      }

      return { success: false }
    } finally {
      setIsLoading(false)
    }
  }

  // NEW: Simple resend function that definitely works
  const resendPasswordResetLink = async (identifier) => {

    if (!identifier) {
      throw new Error('Email or phone number is required')
    }

    try {
      // Method 1: Try the authAPI first - use requestPasswordReset which accepts identifier
      const result = await authAPI.requestPasswordReset(identifier)
      return { success: true, method: 'authAPI', data: result }

    } catch (apiError) {

      try {
        // Method 2: Direct fetch to the API endpoint
        const baseURL = import.meta.env.PROD ? (import.meta.env.VITE_API_URL || 'http://localhost:8000') : '/api'
        const endpoint = `${baseURL}/v1/auth/password/resend-reset`


        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ identifier: identifier })
        })


        if (response.ok) {
          const data = await response.json()
          return { success: true, method: 'fetch', data }
        } else {
          // Handle error response with proper JSON parsing
          let errorData
          try {
            errorData = await response.json()
          } catch (parseError) {
            const errorText = await response.text()
            throw new Error(`API error: ${response.status} - ${errorText}`)
          }

          
          // Show the actual error message from the response
          if (errorData.errors?.message) {
            throw new Error(errorData.errors.message)
          } else if (errorData.message) {
            throw new Error(errorData.message)
          } else {
            throw new Error(`API error: ${response.status}`)
          }
        }

      } catch (fetchError) {

        // Method 3: Mock fallback
        await new Promise(resolve => setTimeout(resolve, 1500)) // Simulate API delay

        return { success: true, method: 'mock', data: { message: 'Email sent (mock)' } }
      }
    }
  }

  // Updated resend email handler using the new function
  const handleResendEmail = async () => {

    if (!userEmail) {
      console.error('❌ userEmail is empty!')
      throw new Error('No email address available for resend')
    }

    setIsResending(true)

    try {
      const result = await resendPasswordResetLink(userEmail)
      return Promise.resolve()

    } catch (error) {
      console.error('❌ Resend failed:', error)
      throw error
    } finally {
      setIsResending(false)
    }
  }

  // Close all modals
  const closeAllModals = () => {
    setShowForgotModal(false)
    setShowCheckEmailModal(false)
    setError('')
    setUserEmail('')
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        background: '#FFFFFF',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        textAlign: 'center',
        maxWidth: '500px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#1F2937',
          marginBottom: '16px'
        }}>
          Forgot Password Demo
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#6B7280',
          marginBottom: '32px'
        }}>
          Click the button below to test the forgot password flow
        </p>

        <button
          onClick={() => setShowForgotModal(true)}
          style={{
            width: '200px',
            height: '56px',
            background: '#3B82F6',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#2563EB'}
          onMouseLeave={(e) => e.target.style.background = '#3B82F6'}
        >
          Forgot Password?
        </button>

        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: '#F3F4F6',
          borderRadius: '12px',
          fontSize: '14px',
          color: '#6B7280'
        }}>
          <strong>API Integration with Fallback:</strong><br />
          Uses real API when available, falls back to mock for demo<br />
          <strong>Test emails:</strong> test@example.com, demo@example.com, user@example.com<br />
          <em>Other emails will show error in mock mode</em>
          <br /><br />
          <strong>Debug Info:</strong><br />
          API Base URL: {import.meta.env.PROD ? (import.meta.env.VITE_API_URL || 'http://localhost:8000') : '/api'}
          <br /><br />
          <button
            onClick={async () => {
              try {
                const result = await resendPasswordResetLink('test@example.com')
              } catch (error) {
                console.error('❌ NEW function test failed:', error)
              }
            }}
            style={{
              padding: '8px 16px',
              background: '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              marginRight: '8px'
            }}
          >
            Test NEW Function
          </button>

          <button
            onClick={async () => {
              try {
                const result = await apiAPI.sendPasswordResetLink({ email: 'test@example.com' })
              } catch (error) {
                console.error('❌ authAPI test failed:', error)
              }
            }}
            style={{
              padding: '8px 16px',
              background: '#EF4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              marginRight: '8px'
            }}
          >
            Test authAPI
          </button>

          <button
            onClick={async () => {
              try {
                const baseURL = import.meta.env.PROD ? (import.meta.env.VITE_API_URL || 'http://localhost:8000') : '/api'
                const response = await fetch(`${baseURL}/v1/auth/password/resend-reset`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ identifier: 'test@example.com' })
                })
                const data = await response.text()
              } catch (error) {
                console.error('❌ Raw fetch failed:', error)
              }
            }}
            style={{
              padding: '8px 16px',
              background: '#8B5CF6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Test Raw Fetch
          </button>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        onSubmit={handleForgotPasswordSubmit}
        onSuccess={() => { }} // handled in submit
        isLoading={isLoading}
        error={error}
      />

      {/* Check Email Modal */}
      <CheckEmailModal
        isOpen={showCheckEmailModal}
        onClose={closeAllModals}
        onResend={handleResendEmail}
        email={userEmail}
        isResending={isResending}
      />
    </div>
  )
}

export default ForgotPasswordDemo